import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as applicationService from '../../services/partnerApplication.service';

/**
 * The Control Room's "Waiting on you" box (Jenn, 2026-10-07; renamed from
 * "To-Do" the same day, since To-Do's means Team365 To-Do's only): new affiliate
 * applications (Approve / Not now) and affiliates who sent in their landing
 * page info. Shows nothing at all when nothing is waiting, so the rest of
 * the Control Room looks exactly as before. Approve sets up their affiliate
 * record and opens it, where "Send intake email" works as it always has.
 */
const when = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function AffiliateWaitingBox() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState('');

  const load = useCallback(() => {
    applicationService.getWaiting().then(setData).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  // Arriving from the home page nudge (#todo) — scroll the box into view once it's there.
  useEffect(() => {
    if (data && window.location.hash === '#todo') document.getElementById('todo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [data]);

  if (!data || (data.applications.length === 0 && data.landingPages.length === 0)) return null;

  const approve = async (a) => {
    setBusy(a.id); setMessage('');
    try {
      const { partnerId } = await applicationService.approveApplication(a.id);
      navigate(a.program === 'partner' ? `/admin/partners/${partnerId}` : `/admin/affiliates/${partnerId}`);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not approve this one.');
      setBusy(null);
    }
  };

  const decline = async (a) => {
    setBusy(a.id); setMessage('');
    try {
      await applicationService.declineApplication(a.id);
      load();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Could not update this one.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section id="todo" className="mb-10 bg-white border border-reset-creamDark rounded-2xl p-6 border-l-[5px] border-l-reset-terracotta">
      <h2 className="font-serif text-xl text-reset-brownDeep mb-1">Waiting on you</h2>
      <p className="text-sm text-gray-500 mb-4">These are also in your Team365 To-Do's.</p>
      {message && <p className="text-sm text-reset-brown bg-reset-cream rounded-lg px-3 py-2 mb-3">{message}</p>}

      {data.applications.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-reset-sage mb-2">New applications</p>
          <ul className="space-y-2">
            {data.applications.map(a => (
              <li key={a.id} className="bg-reset-cream rounded-xl px-4 py-3 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm font-bold text-reset-brownDeep">
                    {a.name} <span className="font-normal text-gray-500">applied to be {a.program === 'partner' ? 'a partner' : 'an affiliate'} · {when(a.created_at)}</span>
                  </p>
                  <p className="text-xs text-gray-500">{a.email}</p>
                  {a.program === 'partner' && (a.practice_name || a.country || a.website_url) && (
                    <p className="text-xs text-gray-500">{[a.practice_name, a.country, a.website_url].filter(Boolean).join(' · ')}</p>
                  )}
                  {a.how_share && <p className="text-sm text-reset-brown mt-1">“{a.how_share}”</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decline(a)} disabled={busy === a.id} className="px-3 py-1.5 border border-gray-200 text-xs font-medium rounded-lg text-gray-600 disabled:opacity-50">Not now</button>
                  <button onClick={() => approve(a)} disabled={busy === a.id} className="px-3 py-1.5 bg-reset-sage text-white text-xs font-medium rounded-lg disabled:opacity-50">{busy === a.id ? 'Working…' : 'Approve'}</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.landingPages.length > 0 && (
        <div>
          <p className="text-xs font-bold text-reset-sage mb-2">Landing page info sent in</p>
          <ul className="space-y-2">
            {data.landingPages.map(p => (
              <li key={p.id} className="bg-reset-cream rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
                <p className="flex-1 min-w-[220px] text-sm text-reset-brownDeep">
                  <span className="font-bold">{p.name}</span> <span className="text-gray-500">sent in their landing page info · {when(p.profile_submitted_at)}</span>
                </p>
                <button onClick={() => navigate(p.partner_type === 'partner' ? `/admin/partners/${p.id}` : `/admin/affiliates/${p.id}`)} className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg shrink-0">Review & approve</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
